<template>
    <div class="wrapper wrapper--no-max-width">
        <header class="row__header">
            <h1>{{ $t(`profile.kyc.level3Text`) }}</h1>
        </header>

        <kyc-form :isCheckboxesEnabled="checkboxesEnabled"
            :isFormProcessing="isFormProcessing"
            :isDocumentUploadEnabled="isDocumentUploadEnabled"
            :isDocumentsEnabled="isDocumentsEnabled"
            :hide-details="reverifyKyc"
        >
            <template v-slot:details>
                <label class="label label--half label--bottom-padding">
                    {{ $t('kycLevel3PostalAddress.streetAddress') }}:

                    <input v-model="form.street"
                        type="text"
                        :placeholder="$t('kycLevel3PostalAddress.streetAddressHint')"
                        :class="['label__input', { 'label__input--active': form.street }]"
                    >
                </label>

                <div :class="['label', 'label--half', 'label--bottom-padding', { 'label--disabled': hasValidatedKycLevel }]">
                    {{ $t('kycLevel3PostalAddress.country') }}:

                    <country-dropdown :country="form.country"
                        :disabled="hasValidatedKycLevel"
                        :placeholder="$t('kycLevel3PostalAddress.countryHint')"
                    />

                    <span v-if="hasValidatedKycLevel"
                        class="label__hint"
                    >
                        {{ $t('kycUserIdentity.countrySubtitle') }}
                    </span>
                </div>

                <label class="label label--half label--bottom-padding">
                    {{ $t('kycLevel3PostalAddress.city') }}:

                    <input v-model="form.city"
                        type="text"
                        :placeholder="$t('kycLevel3PostalAddress.cityHint')"
                        :class="['label__input', { 'label__input--active': form.city }]"
                    >
                </label>

                <label class="label label--half label--bottom-padding">
                    {{ $t('kycLevel3PostalAddress.region') }}:

                    <input v-model="form.region"
                        type="text"
                        :placeholder="$t('kycLevel3PostalAddress.regionHint')"
                        :class="['label__input', { 'label__input--active': form.region }]"
                    >
                </label>

                <label class="label label--half label--no-padding">
                    {{ $t('kycLevel3PostalAddress.postcode') }}:

                    <input v-model="form.zipCode"
                        type="text"
                        :placeholder="$t('kycLevel3PostalAddress.postcodeHint')"
                        :class="['label__input', { 'label__input--active': form.zipCode }]"
                    >
                </label>
            </template>

            <template v-slot:multiselect>
                <div class="label label--half label--document-type">
                    {{ $t('kycDocumentTypes.documentType') }}:

                    <multiselect v-model="form.documentType"
                        :options="documentTypes"
                        :allow-empty="false"
                        :show-labels="false"
                        track-by="label"
                        label="label"
                        :placeholder="$t('kycDocumentTypes.documentTypeHint')"
                        :class="['label__multiselect', { 'label__multiselect--active': form.documentType }]"
                    >
                        <template slot="singleLabel"
                            slot-scope="props"
                        >
                            {{ props.option.label ? props.option.label : form.documentType }}
                        </template>
                        <template slot="option"
                            slot-scope="props"
                        >
                            {{ props.option.label }}
                        </template>
                        <span slot="noResult">{{ $t('registerMobileNumber.noResultsCountries') }}</span>
                    </multiselect>

                    <span class="widget__note">{{ $t('kycLevel3DocumentTypes.note') }}</span>
                </div>

                <kyc-requirements :title="$t('kycLevel3DocumentTypes.requirements')"
                    :items="requirements"
                />
            </template>

            <template v-slot:fileUpload>
                <kyc-guide :steps="steps"
                    :noMaxWidth="true"
                />

                <kyc-files-upload v-on="$listeners"
                    @fileUpload="fileUpload"
                />
            </template>

            <template v-slot:confirmation>
                <kyc-confirmation :checkboxes="checkboxes"
                    :disabled="!form.images.length || isFormProcessing"
                    @submit="submit"
                />
            </template>
        </kyc-form>
    </div>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';

// Local modules
import localStoreTypes from '@webapp/store/types.js';

// Components
import kycForm from '@webapp/components/kyc/kyc-form.vue';
import kycRequirements from '@webapp/components/kyc/kyc-requirements.vue';
import kycConfirmation from '@webapp/components/kyc/kyc-confirmation.vue';
import kycGuide from '@webapp/components/kyc/kyc-guide.vue';
import kycFilesUpload from '@webapp/components/kyc/kyc-files-upload.vue';
import countryDropdown from '@webapp/components/country-dropdown/country-dropdown.vue';

// Helpers
import globalMixin from '@shared/mixins/global-mixin.js';
import postalAddressMixin from '@webapp/helpers/postal-address-mixin.js';
import {
    formatCountry,
} from '@webapp/helpers/country';

export default {
    name: 'kyc-level-three',
    components: {
        kycForm,
        kycRequirements,
        kycConfirmation,
        kycGuide,
        kycFilesUpload,
        countryDropdown,
    },
    mixins: [
        globalMixin,
        postalAddressMixin,
    ],
    data() {
        return {
            form: {
                street: '',
                country: null,
                city: '',
                region: '',
                zipCode: '',
                documentType: '',
                images: [],
            },
            addressId: '',
            documentTypes: [],
            steps: [
                this.$t('kycLevel3Instructions.one'),
                this.$t('kycLevel3Instructions.two'),
            ],
            requirements: [
                this.$t('kycLevel3DocumentTypes.requirement1'),
                this.$t('kycLevel3DocumentTypes.requirement2'),
                this.$t('kycLevel3DocumentTypes.requirement3'),
                this.$t('kycLevel3DocumentTypes.requirement4'),
            ],
            checkboxes: [
                {
                    text: this.$t('kycLevel2PhotoUploadConfirmation.option1'),
                    value: false,
                },
                {
                    text: this.$t('kycLevel2PhotoUploadConfirmation.option2'),
                    value: false,
                },
                {
                    text: this.$t('kycLevel2PhotoUploadConfirmation.option3'),
                    value: false,
                },
            ],
            isProcessing: false,
        };
    },
    computed: {
        ...mapGetters([
            'userDetails',
            'kycDocumentTypes',
            'kycTierLevels',
            'hasUpdatedKycLevelVerification',
            'getLatestValidationError',
            'reverifyKyc',
            'isKycTierLevelValidated',
            'isPartnerApp',
            'getTranslatedStr',
        ]),

        ...mapGetters('countries', ['countriesList']),

        countriesFormattedList() {
            return [...this.countriesList]
                .map((country) => ({
                    name: country.name,
                    iso2: country.iso2,
                }));
        },

        isDocumentsEnabled() {
            return Boolean(this.form.street &&
                this.form.city &&
                this.form.zipCode &&
                this.form.country);
        },

        isDocumentUploadEnabled() {
            return Boolean(this.form.documentType.label);
        },

        checkboxesEnabled() {
            return this.form.images.length > 0;
        },

        isFormProcessing() {
            if (this.reverifyKyc) {
                return false;
            }

            const level = this.kycTierLevels.find((item) => item.tierLevel === 3);

            if (level && (level.status === 'processing' || level.status === 'validated' || this.isProcessing)) {
                return true;
            }

            return false;
        },

        documentsToUpload() {
            const docs = this.form.images;

            return docs.map((document) => {
                document.documentType = this.form.documentType.value;

                return document;
            });
        },

        isFormEdited() {
            return this.form.street !== this.userAddress.street ||
                this.form.zipCode !== this.userAddress.zipCode ||
                this.form.city !== this.userAddress.city ||
                this.form.region !== this.userAddress.region ||
                this.form.country?.iso2 !== this.userAddress.countryCode;
        },

        canAddKycLevelVerification() {
            return (this.isFormEdited && this.documentsToUpload.length > 0) || this.documentsToUpload.length > 0;
        },

        hasValidatedKycLevel() {
            return this.isKycTierLevelValidated(2);
        },
    },
    watch: {
        kycDocumentTypes: 'onKycDocumentTypes',
        hasUpdatedKycLevelVerification: 'onUpdatedKycLevelVerification',
        userAddress: 'onAddressUpdate',
    },
    destroyed() {
        this.setReverifyKyc(false);
    },
    async created() {
        await this.getKycDocumentTypes({
            tierLevel: 3,
        });

        await this.getUserAddress();
        await this.getUserDetails();
    },
    methods: {
        ...mapActions({
            getUserDetails: sharedEpicTypes.GET_USER_DETAILS,
            getKycDocumentTypes: sharedEpicTypes.GET_KYC_DOCUMENTS_TYPES,
            addKycLevelValidation: sharedEpicTypes.ADD_KYC_LEVEL_VERIFICATION,
            setReverifyKyc: localStoreTypes.SET_REVERIFY_KYC,
        }),

        /**
         * Toggle a check box
         *
         * @param {object} payload The item from the list of check boxes selected
         * @param {number} payload.index The index in the list
         * @param {string} payload.value The value as uuid
         * @returns {void}
         */
        toggleCheckBox({
            index,
            value,
        }) {
            this.checkboxes[index].value = value;
        },

        /**
         * Set the files to be uploaded
         *
         * @param {Array} files The files selected
         * @returns {void}
         */
        fileUpload(files) {
            this.form.images = files;
        },

        /**
         * Set the list of document types
         *
         * @param {Array} newTypes The new types given from store
         * @returns {void}
         */
        onKycDocumentTypes(newTypes) {
            // Get the document types
            this.documentTypes = newTypes.map((documentType) => ({
                label: this.getTranslatedStr(documentType, 'name', documentType.name),
                value: documentType.id,
                countryFormats: documentType.kycDocumentTypeCountry || [],
                format: documentType.format,
            }));
        },

        /**
         * On api error
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onError(error) {
            const fetchErrors = this.utilGlobalHandleErrorMessages(error);

            this.$notify({
                type: 'error',
                text: fetchErrors.message,
            });
        },

        /**
         * On api validation
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onValidationError(error) {
            if (Object.keys(error).length) {
                const {
                    city = '',
                    countryCode = '',
                    region = '',
                    street = '',
                    zipCode = '',
                    httpException = '',
                    user = '',
                } = error;

                this.$notify({
                    type: 'error',
                    text: city || countryCode || region || street || zipCode || httpException || user,
                });
            }

            this.showAppLoader({
                isVisible: false,
            });
        },


        /**
         * On updated kyc level verification
         *
         * @param {boolean} newValue The new value received
         * @returns {void}
         */
        onUpdatedKycLevelVerification(newValue) {
            if (newValue) {
                this.$notify({
                    type: 'success',
                    text: this.$t('global.saved'),
                });

                this.isProcessing = true;

                this.showAppLoader({
                    isVisible: false,
                });
            }
        },

        /**
         * On init user address
         *
         * @param {object} address The address details to be set on init
         * @returns {void}
         */
        async onAddressUpdate(address) {
            if (this.canAddKycLevelVerification) {
                await this.addKycLevelValidation({
                    tierLevel: 3,
                    documents: this.documentsToUpload,
                });

                this.form.images = [];
            } else {
                this.form.street = address.street;
                this.form.city = address.city;
                this.form.zipCode = address.zipCode;
                this.form.region = address.region;
                this.form.country = formatCountry(address.countryCode);
            }
        },

        /**
         * Submit current form with current form data
         *
         * @returns {void}
         */
        async submit() {
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            if (this.canAddKycLevelVerification) {
                await this.updateUserAddress({
                    street: this.form.street,
                    city: this.form.city,
                    region: this.form.region || null,
                    zipCode: this.form.zipCode,
                    countryCode: this.form.country.iso2,
                });
            }

            this.setReverifyKyc(false);
        },
    },
};
</script>

<style lang="scss" scoped>
.label {
    &--no-padding {
        margin-right: auto;
    }
}
</style>